import React from 'react';
import { Spinner } from 'src/ui/Spinner';
import {
  useGetUserMatchReachQuery,
  MatchReachResult,
  MRHistogram,
} from 'src/store/vectorProfileApi';
import { Histogram, HistogramBinView } from 'src/ui/charts/Histogram';
import { FunnelBars, FunnelStepView } from 'src/ui/charts/FunnelBars';

const pct1 = (n: number | null | undefined): string =>
  n == null ? '—' : `${Math.round(n * 100)}%`;

const toBins = (h: MRHistogram): HistogramBinView[] =>
  h.bins.map((b) => ({
    from: b.from,
    to: b.to,
    count: b.count,
    inRange: b.inRange,
  }));

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      fontSize: 'var(--fs-2xs)',
      fontWeight: 600,
      letterSpacing: '.02em',
      textTransform: 'uppercase',
      color: 'var(--text-faint)',
      margin: '4px 0 8px',
    }}
  >
    {children}
  </div>
);

function passLine(
  icon: string,
  label: string,
  active: boolean,
  pct: number | null
) {
  return (
    <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>
      {icon} {label} —{' '}
      {active ? (
        <b style={{ color: 'var(--text)' }}>{pct1(pct)} of pool passes</b>
      ) : (
        <span style={{ color: 'var(--text-faint)' }}>no constraint</span>
      )}
    </span>
  );
}

function ReachBody({
  result,
  generatedAt,
}: {
  result: MatchReachResult;
  generatedAt: string;
}) {
  const {
    funnel,
    filters,
    histograms,
    bindingConstraint,
    genderEligible,
    matchable,
  } = result;

  const steps: FunnelStepView[] = funnel.map((s) => {
    let tone: FunnelStepView['tone'] = 'base';
    if (s.key === 'matchable') tone = matchable > 0 ? 'good' : 'bad';
    else if (bindingConstraint && s.key === bindingConstraint.key) tone = 'bad';
    return { label: s.label, count: s.count, pct: s.pct, tone };
  });

  const distStat =
    filters.geo.isDealbreaker || filters.geo.withinRadius > 0
      ? `${pct1(
          genderEligible > 0 ? filters.geo.withinRadius / genderEligible : null
        )} within radius${filters.geo.isDealbreaker ? '' : ' · soft'}`
      : undefined;

  return (
    <div className="col" style={{ gap: 16 }}>
      <SectionLabel>
        Dealbreaker funnel — how each filter shrinks the pool
      </SectionLabel>
      <FunnelBars steps={steps} />

      {bindingConstraint && bindingConstraint.removed > 0 && (
        <div
          style={{
            background: 'var(--red-bg)',
            border: '1px solid var(--red-border)',
            borderRadius: 'var(--radius-md)',
            padding: '10px 12px',
            fontSize: 'var(--fs-xs)',
            color: 'var(--red-text)',
          }}
        >
          ⚠ <b>Binding constraint: {bindingConstraint.label}.</b> Removes{' '}
          {bindingConstraint.removed.toLocaleString('en-US')} of{' '}
          {genderEligible.toLocaleString('en-US')} eligible candidates on its
          own.
        </div>
      )}

      <SectionLabel>
        Pool fact distribution vs. this user’s range (shaded = passes)
      </SectionLabel>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 16,
        }}
      >
        <Histogram
          title="Age"
          subtitle={
            filters.age.active ? 'preferred range' : 'no age preference'
          }
          bins={toBins(histograms.age)}
          unit={histograms.age.unit}
          stat={
            histograms.age.pct != null ? (
              <b>{pct1(histograms.age.pct)} of pool in range</b>
            ) : (
              'no range set'
            )
          }
        />
        <Histogram
          title="Height"
          soft
          subtitle="preferred range"
          bins={toBins(histograms.height)}
          unit={histograms.height.unit}
          stat={
            histograms.height.pct != null ? (
              <>
                <b>{pct1(histograms.height.pct)}</b> in range{' '}
                <span style={{ color: 'var(--text-faint)' }}>
                  (doesn’t gate)
                </span>
              </>
            ) : (
              'no range set'
            )
          }
        />
        <Histogram
          title="Distance"
          subtitle={
            filters.geo.isDealbreaker ? 'radius · dealbreaker' : 'radius · soft'
          }
          bins={toBins(histograms.distanceMiles)}
          unit={histograms.distanceMiles.unit}
          stat={distStat ? <b>{distStat}</b> : 'no radius set'}
        />
      </div>

      <div className="row" style={{ gap: 20, flexWrap: 'wrap' }}>
        {passLine(
          '🚭',
          'Non-smoker required',
          filters.smoking.active,
          filters.smoking.pct
        )}
        {passLine(
          '🙏',
          'Religion required',
          filters.religion.active,
          filters.religion.pct
        )}
      </div>
    </div>
  );
}

/**
 * Per-user "Match Reach": the user's dealbreakers overlaid on the latest pool
 * snapshot. Cheap read; shows an empty state until an admin generates a snapshot
 * (from the Overview page).
 */
export const MatchReach = ({ userId }: { userId: string }) => {
  const { data, isLoading } = useGetUserMatchReachQuery(userId, {
    skip: !userId,
  });

  return (
    <div>
      <div
        className="row"
        style={{
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <span
          style={{
            fontWeight: 600,
            fontSize: 'var(--fs-sm)',
            color: 'var(--text)',
          }}
        >
          Match Reach
        </span>
        {data && data.hasSnapshot && (
          <span
            style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-faint)' }}
          >
            vs pool as of {new Date(data.generatedAt).toLocaleString()}
          </span>
        )}
      </div>

      {isLoading ? (
        <Spinner size="sm" />
      ) : !data || !data.hasSnapshot ? (
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-faint)' }}>
          No pool snapshot yet — generate one from the Overview page to see this
          user’s match reach.
        </span>
      ) : (
        <ReachBody result={data.result} generatedAt={data.generatedAt} />
      )}
    </div>
  );
};
